import { z } from 'zod'

// =============================================================================
// CÓDIGOS OFICIALES MOSSOS D'ESQUADRA (RD 933/2021)
// =============================================================================

/** Tipos de documento oficiales */
export const DocumentTypeEnum = z.enum(['D', 'N', 'P', 'O'])
export type DocumentType = z.infer<typeof DocumentTypeEnum>

/** Códigos de sexo oficiales */
export const SexEnum = z.enum(['M', 'F', 'OTHER'])
export type Sex = z.infer<typeof SexEnum>

/** Códigos de parentesco oficiales (para menores) */
export const ParentescoEnum = z.enum([
  'PM', // Padre/Madre
  'TU', // Tutor/a Legal
  'AB', // Abuelo/a
  'BA', // Bisabuelo/a
  'TI', // Tío/a
  'HR', // Hermano/a
  'CY', // Cónyuge
  'HJ', // Hijo/a
  'NI', // Nieto/a
  'BN', // Bisnieto/a
  'SB', // Sobrino/a
  'CD', // Cuñado/a
  'SG', // Suegro/a
  'YN', // Yerno/Nuera
  'OT', // Otros
])
export type Parentesco = z.infer<typeof ParentescoEnum>

// =============================================================================
// BASE GUEST SCHEMA - Campos comunes a todos los formularios
// =============================================================================

export const BaseGuestSchema = z.object({
  // Identificación personal
  firstName: z.string().min(1, 'validation.firstNameRequired'),
  lastName1: z.string().min(1, 'validation.lastName1Required'),
  lastName2: z.string().optional(),
  
  // Contacto
  email: z.string().email('validation.invalidEmail').optional().or(z.literal('')),
  phone: z.string().optional(),
  mobile: z.string().optional(),
  
  // Fecha de nacimiento
  dateOfBirth: z.coerce.date().optional(),
  
  // Nacionalidad y residencia
  nationality: z.string().length(3, 'validation.nationalityLength').optional(),
  country: z.string().max(2).optional(),
  city: z.string().optional(),
  
  // Sexo
  sex: SexEnum.optional(),
})

// =============================================================================
// PUBLIC GUEST SCHEMA - Formulario público (validación estricta para Mossos)
// =============================================================================

export const PublicGuestSchema = BaseGuestSchema.extend({
  // Sexo - Obligatorio para Mossos (override de BaseGuestSchema)
  sex: SexEnum.refine(val => !!val, { message: 'validation.sexRequired' }),
  
  // Documento - Obligatorio para mayores de 14
  documentType: DocumentTypeEnum.optional(),
  documentNumber: z.string().optional(),
  supportNumber: z.string().optional(), // IDESP para DNI/NIE
  documentExpiry: z.coerce.date().optional(), // Caducidad para Pasaporte
  
  // Parentesco (solo para menores de 18)
  parentesco: ParentescoEnum.optional(),
  
  // Dirección completa (Google Maps)
  addressRaw: z.string().optional(), // JSON completo de Google Maps
  
  // Consentimientos
  isTitular: z.boolean().default(false),
  gdprConsent: z.boolean().refine(val => val === true, {
    message: 'validation.gdprRequired'
  }),
  
  // Firma digital
  signature: z.string().min(1, 'validation.signatureRequired'),
}).superRefine((data, ctx) => {
  // NOTA: lastName2 ja NO és obligatori per a DNI/NIF
  // Molts estrangers nacionalitzats només tenen un cognom
  // El camp és opcional per a tots els tipus de document
  
  // Validación condicional: supportNumber obligatorio para DNI/NIE
  if ((data.documentType === 'D' || data.documentType === 'N') && !data.supportNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'validation.supportRequired',
      path: ['supportNumber'],
    })
  }
  
  // Validación condicional: documentExpiry obligatorio para Pasaporte
  if (data.documentType === 'P' && !data.documentExpiry) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'validation.expirationRequired',
      path: ['documentExpiry'],
    })
  }
})

export type PublicGuest = z.infer<typeof PublicGuestSchema>

// =============================================================================
// ADMIN GUEST SCHEMA - Formulario admin (validación laxa, permite drafts)
// =============================================================================

export const AdminGuestSchema = BaseGuestSchema.extend({
  // IDs de relación
  id: z.string().optional(),
  reservationId: z.string().optional(),
  personId: z.string().optional(),
  
  // Documento - Todo opcional en admin
  documentType: DocumentTypeEnum.optional(),
  documentNumber: z.string().optional(),
  supportNumber: z.string().optional(),
  documentExpiry: z.coerce.date().optional(),
  
  // Parentesco
  parentesco: ParentescoEnum.optional(),
  
  // Dirección
  address: z.string().optional(),
  postalCode: z.string().optional(),
  addressRaw: z.string().optional(),
  
  // Estado
  isTitular: z.boolean().default(false),
  gdprConsent: z.boolean().default(false),
  reportedToMossos: z.boolean().default(false),
  
  // Firma y notas
  signature: z.string().optional(),
  specialRequests: z.string().optional(),
  
  // Fechas de estancia
  checkInDate: z.coerce.date().optional(),
  checkOutDate: z.coerce.date().optional(),
  roomNumber: z.string().optional(),
}).partial() // Todo opcional para permitir drafts

export type AdminGuest = z.infer<typeof AdminGuestSchema>

// =============================================================================
// FORM DATA SCHEMAS - Para react-hook-form
// =============================================================================

/** Schema para el formulario público completo (array de viajeros) */
export const PublicFormSchema = z.object({
  viatgers: z.array(PublicGuestSchema).min(1, 'validation.atLeastOneGuest'),
})

export type PublicFormData = z.infer<typeof PublicFormSchema>

/** Schema para el formulario admin de un solo viajero */
export const AdminGuestFormSchema = AdminGuestSchema

export type AdminGuestFormData = z.infer<typeof AdminGuestFormSchema>

// =============================================================================
// LEGACY SCHEMA - Mantener compatibilidad con código existente
// =============================================================================

/** @deprecated Use PublicGuestSchema or AdminGuestSchema instead */
export const guestWithPersonSchema = z.object({
  reservationId: z.string().min(1, 'Debe seleccionar una reserva'),
  personId: z.string().optional(),
  firstName: z.string().min(1, 'El nombre es obligatorio').optional(),
  lastName: z.string().min(1, 'El apellido es obligatorio').optional(),
  email: z.string().email('Email inválido').optional(),
  phone: z.string().optional(),
  documentType: z.enum(['DNI', 'NIE', 'PASSPORT', 'OTHER']).optional(),
  documentNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  arrivalTime: z.string().optional(),
  specialRequests: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
})

/** @deprecated Use PublicGuest or AdminGuest instead */
export type GuestWithPersonFormData = z.infer<typeof guestWithPersonSchema>

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Transforma datos del formulario público al formato del backend
 */
export function transformPublicGuestToBackend(guest: PublicGuest) {
  return {
    firstName: guest.firstName,
    lastName: `${guest.lastName1}${guest.lastName2 ? ' ' + guest.lastName2 : ''}`,
    lastName1: guest.lastName1,
    lastName2: guest.lastName2 || null,
    email: guest.email || null,
    phone: guest.phone || null,
    mobile: guest.mobile || null,
    dateOfBirth: guest.dateOfBirth?.toISOString() || null,
    nationality: guest.nationality || null,
    sex: guest.sex || null,
    documentType: guest.documentType || null,
    documentNumber: guest.documentNumber || null,
    campSuport: guest.supportNumber || null,
    dataCaducitatDocument: guest.documentExpiry?.toISOString() || null,
    relacioParentesc: guest.parentesco || null,
    city: guest.city || null,
    country: guest.country || null,
    addressRaw: guest.addressRaw || null,
    isPrimary: guest.isTitular,
    gdprConsent: guest.gdprConsent,
    signature: guest.signature,
  }
}

/**
 * Extrae errores de Zod en formato para DebugPanel
 */
export function extractZodErrors(error: z.ZodError) {
  return error.errors.map(err => ({
    path: err.path.map(String),
    message: err.message,
  }))
}
